import BossModel from "@/models/BossModel";
import ProductVersion from "@/models/Product/ProductVersion/ProductVersion";
import Alternative from "@/models/Proposal/Product/Alternative/Alternative";
import Group from "@/models/Proposal/Group/Group";
import Cancellation from "@/models/Proposal/Cancellation/Cancellation";
import AgentPreset from "@/models/Proposal/Product/AgentPreset/AgentPreset";
import CheckState from "@/models/Proposal/CheckState/CheckState";
import Currency from "@/models/Currency/Currency";
import Type from "@/models/Product/Type/Type";
import Detail from "@/models/Proposal/Product/Detail/Detail";
import MarginService from "@/utils/Services/MarginService";
import {round} from "lodash/math";
import moment from "moment";
import Upsell from "@/models/Proposal/Upsell/Upsell";
import Inventory from "@/models/Agent/Inventory/Inventory";
import AgentCode from "@/models/Agent/Code/Code";
import History from "@/models/Proposal/Product/History/History";
import User from "@/models/User/User";

export default class Product extends BossModel {

    static apiUrl = 'proposal/product';

    static entity = 'proposalProducts';

    static fields() {
        return {
            id: this.number(null).nullable(),
            travellers_valid: this.boolean(0),
            product_type_id: this.number(null).nullable(),
            agent_code_id: this.number(null).nullable(),
            proposal_product_alternative_id: this.number(null).nullable(),
            product_version_id: this.number(null).nullable(),
            agent_inventory_id: this.number(null).nullable(),
            proposal_group_id: this.number(null).nullable(),
            proposal_cancellation_id: this.number(null).nullable(),
            proposal_product_agent_preset_id: this.number(null).nullable(),
            proposal_check_state_id: this.number(null).nullable(),
            start_date: this.attr(null),
            end_date: this.attr(null),
            purchase_price: this.number(null),
            sale_price: this.number(null),
            currency_id: this.number(null).nullable(),
            used_exchange_rate: this.number(null),
            local_purchase_price: this.number(null),
            text_intern: this.attr(null),
            text_customer: this.attr(null),
            text_voucher: this.attr(null),
            modified_by_user_id: this.number(null).nullable(),
            breakdown: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            exchange_rate_date: this.attr(null),
            agentCode: this.belongsTo(AgentCode, 'agent_code_id'),
            type: this.belongsTo(Type, 'product_type_id'),
            alternative: this.belongsTo(Alternative, 'proposal_product_alternative_id'),
            productVersion: this.belongsTo(ProductVersion, 'product_version_id'),
            agentInventory: this.belongsTo(Inventory, 'agent_inventory_id'),
            group: this.belongsTo(Group, 'proposal_group_id'),
            cancellation: this.belongsTo(Cancellation, 'proposal_cancellation_id'),
            agentPreset: this.belongsTo(AgentPreset, 'proposal_product_agent_preset_id'),
            checkState: this.belongsTo(CheckState, 'proposal_check_state_id'),
            currency: this.belongsTo(Currency, 'currency_id'),
            details: this.hasMany(Detail, 'proposal_product_id'),
            upsell: this.hasOne(Upsell, 'proposal_product_id'),
            histories: this.hasMany(History, 'proposal_product_id'),
            user: this.belongsTo(User, 'modified_by_user_id'),
        }
    }

    get localPurchasePrice() {
        if (!this.used_exchange_rate) {
            return 0;
        }

        return this.purchase_price * this.used_exchange_rate;
    }

    get marginPercentage() {
        return MarginService.calculateMargePercentage(this.purchase_price,this.sale_price);
    }

    UpdatePrices() {
        return this.constructor.api()
            .action(this.id + '/update-prices/')
            .update();
    }

    Proposal() {
        if (this.getRelated('group')) {
            return this.getRelated('group').Proposal();
        }

        if (this.getRelated('upsell')) {
            return this.getRelated('upsell').Proposal();
        }

        if (this.getRelated('cancellation')) {
            return this.getRelated('cancellation').Proposal();
        }

        return false;
    }

    ProductVersion() {
        return this.getRelated('productVersion');
    }

    Type() {
        return this.getRelated('type');
    }

    Duration() {
        if (this.start_date && this.end_date) {
            let diffInNights = moment(this.end_date).diff(moment(this.start_date), 'days');
            if (diffInNights > 1) {
                return diffInNights;
            }

            return diffInNights
        }

        return null;
    }

    fetchTravalcoPricesPerProduct() {
        return this.fetchTravalcoPrices().then(response => {
            let result = {
                localPurchasePrice: 0,
                purchasePrice: 0,
                salePrice: 0,
                currencyId: 0,
                state: 'unavailable',
            };

            response.forEach(item => {
                result.localPurchasePrice += item.localPurchasePrice;
                result.purchasePrice += item.purchasePrice;
                result.salePrice += item.salePrice;
                result.currency_code = item.currency_code;
            });

            result.localPurchasePrice = round(result.localPurchasePrice, 5);
            result.purchasePrice = round(result.purchasePrice, 2);
            result.salePrice = round(result.salePrice, 2);

            if (response.find(item => item.state === 'unavailable')) {
                result.state = 'unavailable';
            }
            else if (response.find(item => item.state === 'requestable')) {
                result.state = 'requestable';
            }
            else if (response.find(item => item.state === 'available')) {
                result.state = 'available';
            }

            return result;
        });
    }

    static importAgentProduct(proposalId, agentProductId, useStore = true) {
        return this.api().action('import-agent-product/' + proposalId + '/' + agentProductId).create(null, useStore);
    }

    fetchTravalcoPrices(){
        return this.constructor.api().action('travalco/' + this.id + '/prices').disableErrorInPopup().get(false);
    }

    static getSoldProductsFile(params = {}){
        return this.api().action('sold-products/export')
            .filtered(params.filters)
            .getBlob().then((data) => {
                return data;
            });
    }
}
